import React, { useState } from 'react'
import { Loader } from 'lucide-react'
import { apiClient } from '@/utils/api'
import { normalizeStepUpCode, stepUpDecisionError } from '@/utils/sharedDevice'
import { Message } from './Message'

/**
 * A parent enters the S- code shown on a shared device to give it a
 * 10-minute parent window. Used on /step-up and in Settings > Devices.
 */
export const StepUpCodeForm: React.FC<{ autoFocus?: boolean }> = ({ autoFocus }) => {
  const [value, setValue] = useState('')
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null)

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    const code = normalizeStepUpCode(value)
    if (!code) {
      setResult({ tone: 'error', text: 'Enter the code shown on the shared device, for example S-ABC234.' })
      return
    }
    setBusy(true)
    setResult(null)
    try {
      await apiClient.post(`/api/device/step-up/code/${encodeURIComponent(code)}/approve`)
      setValue('')
      setResult({ tone: 'ok', text: 'Approved. The shared device has parent access for the next 10 minutes.' })
    } catch (err: any) {
      setResult({ tone: 'error', text: stepUpDecisionError(err) })
    } finally {
      setBusy(false)
    }
  }

  return (
    <form onSubmit={submit}>
      <label htmlFor="step-up-code" className="block text-sm font-semibold text-gray-800 mb-1">
        Code from the shared device
      </label>
      <div className="flex flex-col sm:flex-row gap-3">
        <input
          id="step-up-code"
          value={value}
          onChange={(e) => {
            setValue(e.target.value.toUpperCase())
            setResult(null)
          }}
          placeholder="S-ABC234"
          autoCapitalize="characters"
          autoCorrect="off"
          spellCheck={false}
          autoFocus={autoFocus}
          className="flex-1 min-w-0 px-4 py-3 text-xl font-mono tracking-widest rounded-xl border-2 border-gray-300 focus:border-indigo-500 outline-none"
        />
        <button
          type="submit"
          disabled={busy || value.trim().length < 6}
          className="min-h-[52px] px-6 rounded-xl bg-indigo-600 text-white font-semibold hover:bg-indigo-700 disabled:opacity-50 focus:outline-none focus:ring-4 focus:ring-indigo-300 inline-flex items-center justify-center gap-2"
        >
          {busy && <Loader className="w-5 h-5 animate-spin" />}
          Approve
        </button>
      </div>
      {result && (
        <div className="mt-3">
          <Message tone={result.tone}>{result.text}</Message>
        </div>
      )}
    </form>
  )
}

export default StepUpCodeForm
